// Feature Flags Configuration
// This file contains all feature flags used throughout the application
//
// IMPORTANT — env-access safety:
// Vite exposes `import.meta.env` as a populated object at runtime. Storybook's
// webpack bundler (used by Chromatic) does NOT — it only inlines specific
// env vars at build time, and `import.meta.env` itself may be undefined in
// the resulting bundle. Reading `.VITE_*` off undefined crashes Chromatic's
// story extraction. We guard with a safe accessor below; all flag reads go
// through `ENV` so they degrade to "undefined" (and therefore the default
// branch of `!== 'false'` etc.) instead of throwing.

// Vite/esbuild statically replace the *bare* token `import.meta.env` with the
// baked env object at build time. Optional chaining (`import.meta?.env`) or any
// aliasing does NOT match that token, so in a production build it degraded to a
// runtime lookup — and the build ships no runtime `import.meta.env` object, so it
// resolved to `undefined`. Result: every `=== 'true'` flag silently read false on
// prod (PLACES_V1, PROGRAM_MODULES, BUILD_TO_EARN_AGREEMENT, SPONSOR_ACCESS_CODES…),
// while the dev server — which serves a live `import.meta.env` — worked. Reference
// the bare token directly so it gets replaced, guarded for non-Vite bundlers
// (Storybook/webpack) where `import.meta.env` may be undefined at runtime.
let _env: Record<string, string | undefined>;
try {
  _env = (import.meta.env as unknown as Record<string, string | undefined>) || {};
} catch {
  _env = {};
}
const ENV: Record<string, string | undefined> = _env;

// Local-dev signal used to auto-enable in-development features without depending
// on per-flag VITE_* env injection. Two independent signals so it can't silently
// fail: Vite's built-in `import.meta.env.DEV` (read via the now-replaced ENV), OR a
// localhost hostname. Prod (a real domain, DEV=false) stays gated by the explicit
// env vars below.
const IS_DEV =
  !!ENV.DEV ||
  (typeof window !== 'undefined' &&
    /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location?.hostname || ''));

export const FEATURE_FLAGS = {
  // Sponsor Link Dialog and Snackbar
  // When disabled, the sponsor link dialog and "Enter sponsor link & code upon checkout" snackbar will not be shown
  ENABLE_SPONSOR_LINK_DIALOG: ENV.VITE_ENABLE_SPONSOR_LINK_DIALOG !== 'false', // Default: true, set to 'false' to disable

  // Sponsor Access Codes (SPONSOR_ACCESS_CODES)
  // Secret sub-codes on a sponsor link + the "disable the public code" switch.
  // Gates the "Access codes" section in SponsorLinkEditorDialog. Auto-ON in local
  // dev; in prod requires the explicit VITE_SPONSOR_ACCESS_CODES env var.
  SPONSOR_ACCESS_CODES: IS_DEV || ENV.VITE_SPONSOR_ACCESS_CODES === 'true', // Default: false in prod

  // Participant Celebration Dialog
  // When disabled, the participant celebration dialog will not be shown after program checkout
  ENABLE_PARTICIPANT_CELEBRATION_DIALOG: true, // Can be moved to env var if needed

  // Deadline Announcement
  // When disabled, the deadline announcement dialog will not be shown
  ENABLE_DEADLINE_ANNOUNCEMENT: false, // Can be moved to env var if needed

  // Programs Nexus Migration
  // When enabled, uses Nexus GraphQL API instead of REST API for My Programs
  // Set to 'true' to enable the new Nexus implementation
  USE_NEXUS_PROGRAMS: ENV.VITE_USE_NEXUS_PROGRAMS === 'true', // Default: false

  // Embedded Stripe Payment
  // When true (default), uses embedded Stripe PaymentElement form (no redirect)
  // When false, falls back to Stripe hosted checkout with redirect
  USE_EMBEDDED_STRIPE_PAYMENT: ENV.VITE_USE_EMBEDDED_STRIPE_PAYMENT !== 'false', // Default: true

  // AIPM Onboarding Kill Switch (Emergency Override)
  // When true, disables ALL AIPM onboarding (retroactive path) regardless of per-program settings
  // Default: false (defers to per-program retroactiveEnabled toggle in AutoPilot config)
  AIPM_ONBOARDING_KILL_SWITCH: ENV.VITE_AIPM_ONBOARDING_KILL_SWITCH === 'true', // Default: false

  // Peachy Rebrand (Scope B+ — /home18 → "Hire Peachy")
  // Wraps the rebranded hero copy, 3-CTA navbar, /plans résumé rewrite, and the
  // m-hero-programs lens-switcher (Programs / People / Places). When false,
  // these surfaces revert to their pre-rebrand behavior so we can roll back
  // without a redeploy. /explore is unaffected either way.
  ENABLE_PEACHY_REBRAND: ENV.VITE_ENABLE_PEACHY_REBRAND !== 'false', // Default: true

  // Express Enroll (Wayne MVP — frictionless one-screen participant join)
  // Master switch for the express-enroll auth mode (name + contact → inline OTP →
  // silent enroll). The REAL per-program gate is the Program.expressEnroll DB field;
  // this flag just lets us kill the feature globally without a redeploy.
  // Default: true (so only programs with expressEnroll:true light it up).
  EXPRESS_ENROLL: ENV.VITE_EXPRESS_ENROLL !== 'false', // Default: true

  // Program Access Policy (adaptive sign-in ladder)
  // Master switch for the access-policy ladder: a Program carries
  // accessPolicy: { floor: 'off'|'verified'|'on', allowUpgrades: boolean } and the
  // public-program auth dialog adapts its primary/secondary actions accordingly
  // (anonymous "Join now" → verified express → full account). Also gates the two
  // creator "Sign-in required?" dials. When false, surfaces fall back to the
  // existing express/standard behavior. Default: true.
  ACCESS_POLICY: ENV.VITE_ACCESS_POLICY !== 'false', // Default: true

  // Program-Link Visitor Tracking (creator "Reach" analytics)
  // Master switch for recording share-link visits (/guide, /public-program,
  // /program) and showing the reach numbers in the share modal + OrganizerGuide.
  // When false, no visits are recorded and the analytics surfaces are hidden —
  // a clean kill switch with no redeploy. Default: true.
  PROGRAM_VISIT_ANALYTICS: ENV.VITE_PROGRAM_VISIT_ANALYTICS !== 'false', // Default: true

  // Program preview link (story PP-3). A signed, expiring token in the URL renders
  // an unpublished program's Guide read-only: no account, no enrollment, nothing
  // written. Gates the /program/preview/:token route only; the mint button and the
  // server query sit behind PROGRAM_PREVIEW_LINK on nexus-prod. "true" exactly.
  PROGRAM_PREVIEW_LINK: ENV.VITE_PROGRAM_PREVIEW_LINK === 'true', // Default: false

  // Coaching (persona scheduling + program coaching policy + booking surface)
  // Master switch for the coaching feature: persona-level scheduling links
  // (Calendly connected / scheduling URL / none), per-program coaching policy
  // (required, cadence, expected calls), the participant booking surface, and
  // coaching status tracking. When false, all coaching surfaces are hidden —
  // a clean kill switch with no redeploy. Default: true.
  ENABLE_COACHING: ENV.VITE_ENABLE_COACHING !== 'false', // Default: true

  // /comms "Who's around" — a live presence roster in the staff inbox that shows who's
  // online / recently present / active today across the staff member's programs, and lets
  // them message any subset. Gated OFF by default (opt-in via VITE_COMMS_PRESENCE=true);
  // the backend query is separately gated by COMMS_PRESENCE. Default: false.
  COMMS_PRESENCE: ENV.VITE_COMMS_PRESENCE === 'true' || IS_DEV, // Default: false (on in dev)

  // Community Connect — consolidates the scattered guide buttons (Coach, Journal,
  // Help, Peers, Team) into one flat module bar at the bottom of the timeline bar.
  // When ON, the module bar renders and the standalone Coach/Journal buttons are
  // hidden (relocated into the bar). Default: true on this branch; flip to
  // default-off (=== 'true') before any production rollout.
  ENABLE_COMMUNITY_CONNECT: ENV.VITE_ENABLE_COMMUNITY_CONNECT !== 'false', // Default: true

  // Participant Comms Thread — replaces the bare `sms:` Help link with a
  // persistent bottom minibar that expands into the participant's unified
  // PilotCity conversation thread (SMS + email + in-app history, single identity,
  // per-message sender labels + program chips). Default OFF — opt-in via
  // VITE_PARTICIPANT_COMMS_THREAD=true until the backend resolvers ship.
  PARTICIPANT_COMMS_THREAD: ENV.VITE_PARTICIPANT_COMMS_THREAD === 'true', // Default: false

  // Universal Inbox — extends the participant comms thread (above) with GROUP
  // conversations: a context switcher (Support · Team · Program) inside the same
  // minibar → panel, backed by the myCommunities / groupThread / postToGroup
  // resolvers and the /api/inbox/stream SSE tap. Additionally gates re-routing the
  // Connect rail's Team/Peers taps into the inbox. Requires PARTICIPANT_COMMS_THREAD
  // to be ON (the panel host). When OFF, only the Support thread shows and Team/Peers
  // open the legacy ParticipantTeamDialog / ParticipantPeersDialog. Default OFF —
  // opt-in via VITE_UNIVERSAL_INBOX=true until the group resolvers ship.
  UNIVERSAL_INBOX: ENV.VITE_UNIVERSAL_INBOX === 'true' || IS_DEV, // Default: false (auto-ON in local dev)

  // Direct Messaging — adds the "start a DM" entry point to the Universal Inbox: a
  // "Message" button on each team roster member opens the inbox's Direct context in a
  // fresh compose targeting that person (the DM thread is lazily created on first send
  // via sendDM). Gates the compose entry point only; receiving DMs already surfaces the
  // Direct tab once someone messages you. Requires UNIVERSAL_INBOX (its host). Default
  // OFF — opt-in via VITE_DIRECT_MESSAGING=true until the DM resolvers ship.
  DIRECT_MESSAGING: ENV.VITE_DIRECT_MESSAGING === 'true' || IS_DEV, // Default: false (auto-ON in local dev)

  // Creator Inbox (Slice 2) — the unified creator lens that folds /monitor ›
  // communications (approval queue, 1:1 participant conversations, escalations,
  // engagement stats, tags/assignment) into the Inbox, reading the converged clean
  // model. Umbrella flag for the whole Slice-2 epic; participant-facing support-thread
  // redesign also rides this. Default OFF — opt-in via VITE_CREATOR_INBOX=true until the
  // lens ships + the minor-safety sign-off clears.
  CREATOR_INBOX: ENV.VITE_CREATOR_INBOX === 'true' || IS_DEV, // Default: false (auto-ON in local dev)

  // Community Surface — the binary "network" experience layered onto the Universal
  // Inbox: a merged People + Chat surface inside the minibar (split roster + thread),
  // an in-surface PersonCard (Message / Wave / View profile), a "Wave" nudge, the
  // "connection moment" celebratory toast (SSE-driven), and the two binary sections on
  // /portfolio/people ("Your network" + "People you may know"). When OFF, the minibar,
  // presence panel, and /portfolio/people are byte-for-byte unchanged from today.
  //
  // IMPLIES the inbox + DM surfaces: COMMUNITY_SURFACE semantically IS the Universal
  // Inbox + Direct Messaging, so turning it ON self-sufficiently mounts the minibar host
  // and enables UNIVERSAL_INBOX / DIRECT_MESSAGING behavior (ParticipantCommsThread +
  // TimelineBar OR-in COMMUNITY_SURFACE). No need to also set VITE_UNIVERSAL_INBOX /
  // VITE_DIRECT_MESSAGING in prod — flipping ONLY VITE_COMMUNITY_SURFACE=true renders the
  // full coherent feature.
  //
  // Auto-ON in local dev (matches UNIVERSAL_INBOX/DIRECT_MESSAGING) so the surface is testable
  // without depending on the flaky per-flag VITE_* injection; prod (real domain, IS_DEV=false)
  // stays gated by VITE_COMMUNITY_SURFACE=true.
  COMMUNITY_SURFACE: ENV.VITE_COMMUNITY_SURFACE === 'true' || IS_DEV, // Default: false (auto-ON in local dev)

  // Ecosystem World — a flag-gated "Membership" panel in the Ecosystem Framer that
  // surfaces an ecosystem's Audiences with live counts + a drill-down member list
  // (avatar + name). Additive/dark: when OFF, EcosystemView is byte-for-byte
  // unchanged (no Membership tab, no membership queries). Auto-ON in local dev
  // (matches COMMUNITY_SURFACE) so the panel is testable without relying on the
  // flaky per-flag VITE_* injection; prod (real domain, IS_DEV=false) stays gated
  // by VITE_ECOSYSTEM_WORLD=true.
  ECOSYSTEM_WORLD: ENV.VITE_ECOSYSTEM_WORLD === 'true' || IS_DEV, // Default: false (auto-ON in local dev)

  // Ecosystem Minibar Layer (C2) — the ECOSYSTEM LAYER inside the participant minibar
  // (ParticipantCommsThread): world-grouped room lists in the Program/Team tabs (each
  // world headed by its openable Townsquare room), a slim world-chip row at the top of
  // the expanded panel, "In {world}" grouping on the Direct tab, and per-world unread
  // rollups. Rides the BE's flag-gated payload extensions (CommunitySummary.ecosystems,
  // synthetic contextType:'ecosystem' Townsquare rows, DM inEcosystemIds) and the
  // existing ecosystemRoom / postToGroup('ecosystem', …) contract. Additive/dark: when
  // OFF — or when the payload carries no ecosystem fields / the user is a citizen of no
  // world — the minibar renders byte-for-byte as today. Auto-ON in local dev (matches
  // ECOSYSTEM_WORLD); prod (real domain, IS_DEV=false) stays gated by
  // VITE_ECOSYSTEM_MINIBAR_LAYER=true.
  ECOSYSTEM_MINIBAR_LAYER: ENV.VITE_ECOSYSTEM_MINIBAR_LAYER === 'true' || IS_DEV, // Default: false (auto-ON in local dev)

  // Ecosystem Surfaces (Wave-4b) — the NEW MOUNTS of the minibar beyond the participant
  // guide, each declared through a typed SURFACE CONFIG (surfaceConfig.ts): the
  // /portfolio world-first hub (Worlds · Direct · PilotCity support, all roles), the
  // Organizer Guide minibar (creator lens: PilotCity support · organizer team · Direct ·
  // Worlds), and the /monitor overlay. Gates ONLY the new mounts + the `surface` prop
  // being honored — when OFF (or when no surface config is passed) every existing mount
  // renders byte-for-byte as today: no new mounts, minibar behavior unchanged everywhere.
  // Auto-ON in local dev (matches the ECOSYSTEM_* family); prod (real domain,
  // IS_DEV=false) stays gated by VITE_ECOSYSTEM_SURFACES=true.
  ECOSYSTEM_SURFACES: ENV.VITE_ECOSYSTEM_SURFACES === 'true' || IS_DEV, // Default: false (auto-ON in local dev)

  // Ecosystem Portal (C3+C4) — the FULL-PAGE version of the minibar: /square/:ecosystemId?
  // renders the world as one screen (rooms nav on the left, the selected room's thread —
  // feed-interleaved with world MOMENTS for Townsquares — in the center, People +
  // Downtown panels on the right). Also gates the tiny "expand" doorway button in the
  // minibar panel header that deep-links into /square/:currentWorld. Additive/dark:
  // when OFF the /square route redirects home and the doorway button never renders —
  // every existing surface is byte-for-byte unchanged. Auto-ON in local dev (matches
  // ECOSYSTEM_WORLD / ECOSYSTEM_MINIBAR_LAYER); prod (real domain, IS_DEV=false) stays
  // gated by VITE_ECOSYSTEM_PORTAL=true.
  ECOSYSTEM_PORTAL: ENV.VITE_ECOSYSTEM_PORTAL === 'true' || IS_DEV, // Default: false (auto-ON in local dev)

  // Ecosystem Settings (Wave 4c) — the governance room's CONFIG PANEL: two new organs
  // inside GovernWorldDialog — Sections ("who's in this world": participants /
  // organizers / sponsors rows with member counts + a Rebuild action) and World
  // settings (posting policy, profanity filter, out-of-app notification default,
  // moments density, membership exceptions) — plus the @pilotcity.com staff override
  // on the Govern door and the "Issuing people" selector in program reward settings.
  // Additive/dark: when OFF every surface renders byte-for-byte as today (no new
  // organs, no settings/sections queries, no staff door, no issuing-people block).
  // Auto-ON in local dev (matches the ECOSYSTEM_* family); prod (real domain,
  // IS_DEV=false) stays gated by VITE_ECOSYSTEM_SETTINGS=true.
  ECOSYSTEM_SETTINGS: ENV.VITE_ECOSYSTEM_SETTINGS === 'true' || IS_DEV, // Default: false (auto-ON in local dev)

  // Connect rules (story 2.2) — a THIRD organ inside GovernWorldDialog: the
  // ecosystem's Connect settings (who may invite, what a guest sees, whether
  // there is a people deck at all, provisional cards, and the three notify
  // switches), read from and written to Ecosystem.settings.connect.
  //
  // MUST BE PAIRED WITH THE BACKEND FLAG. The panel renders only once the server
  // actually returns a `connect` key, so against an API without
  // CONNECT_ECOSYSTEM_SETTINGS the organ is simply ABSENT — no note, no error.
  // (An earlier version of this comment promised an "unavailable note" that was
  // never built. Absent is the right behaviour; the comment was the bug.)
  //
  // It does NOT depend on VITE_ECOSYSTEM_SETTINGS. It did, accidentally, in the
  // first cut: the loader that fetches worldSettings was pushed only when that
  // flag was on, so turning this one on alone — the obvious rollout, since
  // ECOSYSTEM_SETTINGS is dark in prod — left the panel permanently invisible
  // with nothing in the console. Local dev hid it because ECOSYSTEM_SETTINGS is
  // `|| IS_DEV`.
  //
  // NOTHING ENFORCES THESE SETTINGS YET. Writing `audienceMode: 'open'` changes
  // no behaviour until story 7.2 wires the chokepoints; the panel says so in as
  // many words, because a founder flipping an age switch and believing it took
  // effect is a worse failure than the switch not existing.
  //
  // NOT auto-ON in dev, unlike the ECOSYSTEM_* family: this writes real rows on
  // whatever cluster the dev API points at.
  CONNECT_ECOSYSTEM_SETTINGS: ENV.VITE_CONNECT_ECOSYSTEM_SETTINGS === 'true', // Default: false

  // Govern home (story 6.1) — governance gets a ROUTE, /ecosystem/:id/govern,
  // instead of existing only as a dialog nobody can link to. The shell plus the
  // Overview and On-site tabs; stories 6.3-6.6 move one organ at a time.
  //
  // The existing GovernWorldDialog is OPENED from it, never duplicated, so
  // there is no moment where two screens edit the same charter. Off = the route
  // renders a "not switched on" note and nothing links to it; every existing
  // surface behaves exactly as today.
  GOVERN_HOME: ENV.VITE_GOVERN_HOME === 'true', // Default: false

  // Account merge review page (stories 4.3-4.5) - /staff/merge. Staff only;
  // the server refuses everyone else and refuses everything while its own
  // ACCOUNT_MERGE is off. OFF here = the route renders a "not switched on"
  // note and nothing links to it.
  ACCOUNT_MERGE: ENV.VITE_ACCOUNT_MERGE === 'true', // Default: false

  // Ecosystem redirects (story 9.4) — the World page and the Square send people
  // to Connect, for ecosystems that actually HAVE a Connect network.
  //
  // WHY THE PAGES ARE NOT DELETED. Four people surfaces exist and almost nobody
  // uses three of them (the Townsquare has one message, ever). Connect is where
  // people are. But a world with no Connect network has nowhere to be sent, so
  // the redirect is conditional on the server saying `connect.hasNetwork`, not
  // on the flag alone.
  //
  // THREE ROUTES MUST KEEP WORKING and are never redirected:
  //   /ecosystem/s/:token   a share link somebody was sent
  //   /ecosystem/:id/plan   the blueprint workspace
  //   /ecosystem/:id/govern the governance home (story 6.1)
  // Redirecting any of those would break a link that is already in the world.
  ECOSYSTEM_REDIRECTS: ENV.VITE_ECOSYSTEM_REDIRECTS === 'true', // Default: false

  // Program Modules Framework (Places/Program-Modules epic — Wave 2) — the PARALLEL
  // module-descriptor layer: extends the useProgramModules registry into full module
  // descriptors (panel + onboardingPhase + policyLoader + nudge), adds the optional
  // nudge-badge slot to ProgramModuleBar, the shared trigger runner (useModuleTriggers),
  // and the module-contributed onboarding phase-list builder. Additive/dark: when OFF,
  // the existing hand-wired modules (coach/journal/team/share/leave) behave EXACTLY as
  // today — ProgramModuleBar renders the same DOM (empty nudge map), useProgramModules'
  // existing exports are untouched, and useAIPMOnboarding's phase list is byte-for-byte
  // the hardcoded array. The framework is a parallel layer, never an in-place rewrite.
  // Auto-ON in local dev (matches the family pattern); prod (real domain, IS_DEV=false)
  // stays gated by VITE_PROGRAM_MODULES=true.
  PROGRAM_MODULES: ENV.VITE_PROGRAM_MODULES === 'true' || IS_DEV, // Default: false (auto-ON in local dev)

  // Places (Places/Program-Modules epic — Wave 3) — the in-person facilitation
  // module: the Places chip in the participant module bar, the PlacesCard panel
  // (map pin + directions + arrival instructions + contacts/supervisors + hours +
  // schedule + attendance history + check-in/out), the PlacesOnboardingPhase, and
  // the module's registration on the shared trigger runner. Requires PROGRAM_MODULES
  // (the framework host). Additive/dark: when OFF (or when a program references no
  // Place), the module bar, timeline bar, and onboarding are byte-for-byte unchanged —
  // the host never sets `placesAvailable`, the descriptor stays a thin wrap, and the
  // runner has no Places registrant. Auto-ON in local dev (matches the family pattern);
  // prod (real domain, IS_DEV=false) stays gated by VITE_PLACES_V1=true.
  PLACES_V1: ENV.VITE_PLACES_V1 === 'true' || IS_DEV, // Default: false (auto-ON in local dev)

  // Participant exports (Summer-2026 #6/#7) — "Export my report" (print-PDF portfolio
  // report at /program-report) and "Export deck" (16:9 slides at /program-deck, PDF +
  // .pptx via pptxgenjs). Gates the entry buttons in the guide SettingsPanel
  // Share-access section and Monitor ParticipantDetail; the routes are requiresAuth
  // and the BE queries are authz-guarded regardless (participant = own report only;
  // monitor variant = organizer incl. coordinator / stakeholder / auto-monitor /
  // super-admin). OFF => buttons hidden, surfaces byte-identical.
  PARTICIPANT_REPORT_EXPORT: ENV.VITE_PARTICIPANT_REPORT_EXPORT === 'true' || IS_DEV, // Default: false (auto-ON in local dev)

  // Build-to-Earn Agreement (clone of the Offer agreement flow) — gates the creator
  // toggle ("Build projects to earn internships — require a signed agreement to
  // begin") in m-checkout-form and the participant-facing build-to-earn signing
  // surfaces (m-default-adk-start-agreement + /sign-start/:studentId), which talk to
  // the REST clone `/api/start-agreement`. Auto-enabled in LOCAL DEV (|| IS_DEV) like
  // the ECOSYSTEM_*/PLACES/comms families — the indirect `ENV.VITE_*` read doesn't
  // resolve reliably in dev, so the explicit env var alone left it dark on localhost.
  // PROD (real domain, IS_DEV=false) stays gated on VITE_BUILD_TO_EARN_AGREEMENT=true,
  // preserving the legal hold. When OFF: no creator toggle, no participant block,
  // validationRegistry unchanged — every legacy path byte-identical.
  BUILD_TO_EARN_AGREEMENT: ENV.VITE_BUILD_TO_EARN_AGREEMENT === 'true' || IS_DEV, // Default: false (auto-ON in local dev)

  // Comms Simplify — declutters the community minibar into one clean composition:
  // renames the "Direct" tab to "Messages" (default + leftmost everywhere), folds the
  // "Support" tab into Messages as a single pinned "Help" row on top, and relabels the
  // "PilotCity" chat counterparty to "Help". PURELY PRESENTATION: each surface keeps its
  // existing Help data source (no backend repoint) — a true source-merge is a later
  // Eric-reviewed follow-up. Auto-ON in local dev (|| IS_DEV) like the ECOSYSTEM_*/PLACES
  // families; PROD (real domain, IS_DEV=false) stays gated on VITE_COMMS_SIMPLIFY=true.
  // When OFF: tabs, labels, and support routing are byte-identical to today.
  COMMS_SIMPLIFY: ENV.VITE_COMMS_SIMPLIFY === 'true' || IS_DEV, // Default: false (auto-ON in local dev)

  // Team Onboarding — surfaces the Team module as a SOFT, optional onboarding phase in the
  // AIPM onboarding dialog (after Coaching / before Goal), mirroring the Coaching phase. The
  // phase only appears when the team module is enabled for the program, the creator opted in
  // (gate.promptTiming === 'onboarding'), and the participant isn't already on a team — and it
  // is ALWAYS skippable (never blocks). Also gates the creator toggle in AutoPilotSettingsDialog
  // that flips promptTiming. Requires PROGRAM_MODULES (the team module host). Opt-in / default
  // OFF in prod (matches PARTICIPANT_COMMS_THREAD's posture) — when OFF, shouldShowTeam is false,
  // the 'team' phase is filtered out of phaseOrder, and onboarding is byte-identical to today.
  TEAM_ONBOARDING: ENV.VITE_TEAM_ONBOARDING === 'true', // Default: false

  // Student Instances — lets a participant start a brand-new "version" (a fresh
  // Student doc for the same participant+program) from the Versions dialog, and
  // makes the Versions rail reachable even at a single instance so they can create
  // a second. Additive/dark: when OFF, the "Start a fresh version" button is hidden
  // and the Versions rail keeps its existing multi-instance-only gate — every
  // surface is byte-for-byte unchanged. Auto-ON in local dev (matches the
  // ECOSYSTEM_*/PLACES families); prod (real domain, IS_DEV=false) stays gated on
  // VITE_STUDENT_INSTANCES=true.
  STUDENT_INSTANCES: ENV.VITE_STUDENT_INSTANCES === 'true' || IS_DEV, // Default: false (auto-ON in local dev)

  // Presence Analytics — time-on-platform reporting for participants. Gates the per-person
  // "time this week" column + 7-day sparkline (and time-sort) in the /comms "Who's around"
  // roster, plus the ParticipantTimeReportCard (total active hours, per-program/per-activity
  // breakdown, active days, CSV export) and its "Time / engagement" entry point in the
  // /monitor participant detail. Backed by the participantTimeReport / participantTimeReportCsv
  // / cohortWeeklyTime resolvers (separately gated server-side). Additive/dark: when OFF, the
  // roster is byte-for-byte unchanged, no report card mounts, and no analytics queries fire.
  // Auto-ON in local dev (matches the ECOSYSTEM_*/PLACES families); prod (real domain,
  // IS_DEV=false) stays gated on VITE_PRESENCE_ANALYTICS=true.
  PRESENCE_ANALYTICS: ENV.VITE_PRESENCE_ANALYTICS === 'true' || IS_DEV, // Default: false (auto-ON in local dev)

  // AutoConnect — masked SMS relay "warm introductions" Connection tab in /comms
  // Compose. Staff broker a private relay between two people (numbers stay hidden).
  // Dark by default; backend also gated by AUTOCONNECT_ENABLED on Nexus.
  ENABLE_AUTOCONNECT: ENV.VITE_ENABLE_AUTOCONNECT === 'true', // Default: false

  // SMS Event RSVP via Event Tags — "event tag" = CommsTag + event block. Gates the
  // TagManager event section, the TagSidebar calendar icon + confirmed-count badge,
  // and the EventTagStrip (RSVP roster) above the tag-filtered conversation list.
  // Backend separately gated by EVENT_RSVP on Nexus (commsTagEvent returns
  // {enabled:false} when off). Dark by default, incl. local dev — opt-in via
  // VITE_EVENT_RSVP=true. When OFF every /comms surface is byte-for-byte unchanged.
  EVENT_RSVP: ENV.VITE_EVENT_RSVP === 'true', // Default: false

  /**
   * ALUMNI_REUNION — the public /reunion event page (PilotCity 1,000 Strong).
   *
   * Dark by default. The route is PUBLIC, so shipping it unflagged would put a
   * live page at pilotcity.com/reunion the moment this merges — and until the
   * REUNION event tag exists in prod, every RSVP would fail "event not found".
   * Enable only after the tag is minted and smoke-tested end to end.
   * Backend is separately gated by EVENT_RSVP on Nexus.
   */
  ALUMNI_REUNION: ENV.VITE_ALUMNI_REUNION === 'true', // Default: false

  // Scala-42 — private 2-player card game at /scala (House-of-Eden variant of
  // Scala 40: 13 cards, 42-point opening, endless win tally). Public route with
  // room-code + player-token access; backend separately gated by
  // SCALA_GAME_ENABLED on Nexus. Auto-ON in local dev; prod gated by VITE_SCALA.
  SCALA: ENV.VITE_SCALA === 'true' || IS_DEV, // Default: false (auto-ON in local dev)

  // Activity auto-save (BPEI participant-experience plan, Track 3). A participant's
  // typed work is persisted in the background by ONE careful writer
  // (composables/useActivityAutosave: read-merge-write of touched fields only, never
  // `completed`, no success tail). OFF => Renderer never instantiates the writer and
  // the SAVE/COMPLETE bar behaves exactly as today. Deliberately NOT auto-ON in local
  // dev: the wire protocol is a whole-ADK replace and the rollout is program-by-program.
  // Companion allowlist: VITE_ACTIVITY_AUTOSAVE_PROGRAMS = '*' or comma-separated
  // Program ids (constants/activityAutosave.ts). Registry: Nexus docs/feature-flags.yaml
  // `activityAutosave`.
  ACTIVITY_AUTOSAVE: ENV.VITE_ACTIVITY_AUTOSAVE === 'true', // Default: false
  ACTIVITY_AUTOSAVE_PROGRAMS: ENV.VITE_ACTIVITY_AUTOSAVE_PROGRAMS || '', // Default: '' (nobody)

  // Join peek (BPEI plan, Track 6 experiment 2). Funnel measurement 2026-09-13:
  // 56 external visitors reached the Kaiser guide in 48 h and 0 created an account;
  // the sign-in box is non-dismissible, so nobody saw the program before being asked
  // for an email. On allowlisted programs the box can be dismissed ("Look around
  // first"), the guide renders read-only behind it and a sticky bar offers Join.
  // '*' or comma-separated Program ids (constants/joinPeek.ts). OFF => unchanged.
  JOIN_PEEK_PROGRAMS: ENV.VITE_JOIN_PEEK_PROGRAMS || '', // Default: '' (nobody)

  // Landing-to-checkout funnel events (composables/useFunnelTracker -> Nexus
  // POST /api/funnel/events; organizer summary in the Settings panel). ON by default;
  // kill switch only. Anonymous visitor id + per-tab session, never PII.
  FUNNEL_ANALYTICS: ENV.VITE_FUNNEL_ANALYTICS !== 'false', // Default: true

  // Landing sub-actions under the first ask (2026-09-14): "Send this to a parent" (native
  // share / copy) and "Text it to yourself" (sms: link) - no account, no backend; the
  // multi-visit readers of the YouTube campaign never typed a contact, and $99 is a
  // parent's decision. Kill switch only.
  LANDING_SHARE: ENV.VITE_LANDING_SHARE !== 'false' // Default: true
} as const;

// Type for feature flag keys
export type FeatureFlagKey = keyof typeof FEATURE_FLAGS;

// Helper function to check if a feature flag is enabled
export function isFeatureEnabled(flagKey: FeatureFlagKey): boolean {
  return FEATURE_FLAGS[flagKey];
}
